import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./ooraInfraDataset.css";
import { addOoraInfraDataset } from "../../Redux/Actions/ooraInfraDatasetActions";
import AddImage from "../../Global Functions/addImage";
import { EMPTY_FORM, validateForm, buildPayload } from "../../Utils/ooraInfraDatasetHelpers";
import OoraInfraMetaFields from "./OoraInfraMetaFields";

const EMPTY_DOC = { url: "", title: "", description: "", fileName: "", uploading: false };

const AddOoraInfraDataset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addLoading = false } = useSelector((state) => state.ooraInfraDataset || {});

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [documents, setDocuments] = useState([{ ...EMPTY_DOC }]);

  const anyUploading = documents.some((d) => d.uploading);
  const busy = addLoading || anyUploading;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocChange = (index, field, value) => {
    setDocuments((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const addDocRow = () => setDocuments((prev) => [...prev, { ...EMPTY_DOC }]);

  const removeDocRow = (index) => setDocuments((prev) => prev.filter((_, i) => i !== index));

  const handleFile = async (index, file) => {
    if (!file) return;
    handleDocChange(index, "uploading", true);
    try {
      const url = await AddImage(file);
      setDocuments((prev) =>
        prev.map((d, i) =>
          i === index
            ? { ...d, url, fileName: file.name, uploading: false, title: d.title || file.name.replace(/\.[^/.]+$/, "") }
            : d
        )
      );
    } catch (error) {
      handleDocChange(index, "uploading", false);
      toast.error("File upload failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) return toast.error(error);
    for (let i = 0; i < documents.length; i++) {
      if (!documents[i].url) return toast.error(`Document ${i + 1}: please upload a file`);
      if (!documents[i].title.trim()) return toast.error(`Document ${i + 1}: title is required`);
    }

    const response = await dispatch(
      addOoraInfraDataset({
        ...buildPayload(form),
        documents: documents.map((d) => ({
          url: d.url,
          title: d.title.trim(),
          description: d.description.trim(),
        })),
      })
    );

    if (response?.statusCode === 200) {
      toast.success("Dataset added successfully");
      navigate("/oora-infra-dataset");
    } else {
      toast.error(response?.message || "Something went wrong");
    }
  };

  return (
    <>
      <div className="oid-wrapper">
        <div className="oid-header">
          <div className="oid-header-left">
            <div className="oid-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            </div>
            <div>
              <h1 className="oid-title">Add Dataset</h1>
              <p className="oid-subtitle">Upload infrastructure documents for a location</p>
            </div>
          </div>
        </div>

        <form className="oid-card" onSubmit={handleSubmit}>
          <div className="oid-form-body">
            <OoraInfraMetaFields form={form} onChange={handleChange} disabled={busy} />

            <div className="oid-docs-head">
              <h3>Documents</h3>
              <button type="button" className="oid-btn oid-btn--ghost oid-btn--sm" onClick={addDocRow} disabled={busy}>
                + Add Document
              </button>
            </div>

            {documents.map((doc, index) => (
              <div className="oid-doc" key={index}>
                <div className="oid-doc-top">
                  <span className="oid-section-badge">{String(index + 1).padStart(2, "0")}</span>
                  {documents.length > 1 && (
                    <button type="button" className="oid-remove" onClick={() => removeDocRow(index)} disabled={busy}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="oid-grid-2">
                  <div className="oid-field">
                    <label className="oid-label">Document Title <span className="oid-req">*</span></label>
                    <input
                      className="oid-input"
                      type="text"
                      value={doc.title}
                      onChange={(e) => handleDocChange(index, "title", e.target.value)}
                      placeholder="e.g. Sector Layout Plan"
                      disabled={busy}
                    />
                  </div>
                  <div className="oid-field">
                    <label className="oid-label">Description <span className="oid-optional">(Optional)</span></label>
                    <input
                      className="oid-input"
                      type="text"
                      value={doc.description}
                      onChange={(e) => handleDocChange(index, "description", e.target.value)}
                      placeholder="Short description"
                      disabled={busy}
                    />
                  </div>
                  <div className="oid-field oid-field--full">
                    <label className="oid-label">File <span className="oid-req">*</span></label>
                    <label className={`oid-upload ${doc.url ? "oid-upload--done" : ""}`}>
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          handleFile(index, e.target.files[0]);
                          e.target.value = "";
                        }}
                        disabled={busy}
                      />
                      {doc.uploading ? (
                        <span>Uploading...</span>
                      ) : doc.url ? (
                        <span className="oid-file-name">{doc.fileName} — click to replace</span>
                      ) : (
                        <span>Click to choose a file</span>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="oid-form-foot">
            <button type="button" className="oid-btn oid-btn--ghost" onClick={() => navigate("/oora-infra-dataset")} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="oid-btn oid-btn--submit" disabled={busy}>
              {addLoading ? "Saving..." : "Save Dataset"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddOoraInfraDataset;